<div
  #trigger
  cdkOverlayOrigin
  #overlayOrigin="cdkOverlayOrigin"
  class="sg-auto-complete-select__container"
  [class.sg-auto-complete-select--focused]="focused || isOpen"
  [class.sg-auto-complete-select--floating]="!empty || isOpen || searchQuery"
  [class.sg-auto-complete-select--has-prefix]="prefixIcon"
  [class.sg-auto-complete-select--clearable]="clearable"
  [class.sg-auto-complete-select--disabled]="disabled"
  [class.sg-auto-complete-select--invalid]="isInvalid"
  [class.sg-auto-complete-select--small]="size === 'small'"
  [class.sg-auto-complete-select--secondary]="variant === 'secondary'"
  [class.sg-auto-complete-select--underline]="variant === 'underline'"
  [class.sg-auto-complete-select--open]="isOpen"
  tabindex="0"
  role="combobox"
  [attr.aria-expanded]="isOpen"
  [attr.aria-haspopup]="'listbox'"
  [attr.aria-disabled]="disabled"
  [attr.aria-invalid]="isInvalid"
  (click)="toggle()"
  (focus)="onFocus()"
  (blur)="onBlur()">
  <!-- The outline structure -->
  <div class="sg-auto-complete-select__outline">
    <div class="sg-auto-complete-select__outline-start"></div>
    <div class="sg-auto-complete-select__outline-notch"></div>
    <div class="sg-auto-complete-select__outline-end"></div>
  </div>
  <!-- Floating label -->
  @if (label) {
    <label
      class="sg-auto-complete-select__label"
      [class.sg-label-medium-regular]="empty && !isOpen && !searchQuery"
      [class.sg-label-small-regular]="!empty || isOpen || searchQuery">
      {{ label }}
      @if (isRequired && !disabled) {
        <span class="sg-auto-complete-select__required">*</span>
      }
    </label>
  }
  <!-- Trigger content wrapper -->
  <div class="sg-auto-complete-select__wrapper">
    <!-- Content projection: Loading spinner before trigger (for entity-select-auto-complete) -->
    <ng-content select="[sgAutoCompleteSelect2BeforeTrigger]"></ng-content>
    @if (prefixIcon) {
      <mat-icon class="sg-auto-complete-select__prefix">{{ prefixIcon }}</mat-icon>
    }
    <!-- Custom trigger template or default display -->
    @if (triggerDirective && !empty) {
      <ng-container *ngTemplateOutlet="triggerDirective.templateRef; context: triggerContext"></ng-container>
    } @else {
      @if (!empty) {
        <span class="sg-auto-complete-select__value" [class.sg-body-medium-regular]="size !== 'small'" [class.sg-body-small-regular]="size === 'small'">
          <!-- Single select display -->
          @if (!multiple) {
            {{ displayValue }}
          }
          <!-- Multi-select: Count mode -->
          @if (multiple && selectedCount > 0 && multiSelectDisplay === "count") {
            <span
              class="sg-auto-complete-select__count-display"
              [class.sg-body-medium-regular]="size !== 'small'"
              [class.sg-body-small-regular]="size === 'small'">
              {{ selectedCount }} selected
            </span>
          }
          <!-- Multi-select: Chips mode -->
          @if (multiple && selectedCount > 0 && multiSelectDisplay === "chips") {
            <div class="sg-auto-complete-select__chips">
              <!-- Stacked indicator (shows count of additional items) -->
              @if (additionalSelectedCount > 0) {
                <div class="sg-auto-complete-select__chip sg-auto-complete-select__chip--stacked" [sgTooltip]="selectedLabelsTooltip">
                  <span class="sg-auto-complete-select__chip-count sg-label-small-regular">+{{ additionalSelectedCount }}</span>
                  <button
                    type="button"
                    class="sg-auto-complete-select__chip-remove"
                    tabindex="-1"
                    (mousedown)="$event.preventDefault()"
                    (click)="clearAllButLast($event)"
                    aria-label="Remove additional selections">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              }
              <!-- Last selected chip -->
              @if (lastSelectedOption) {
                <div class="sg-auto-complete-select__chip">
                  <span class="sg-auto-complete-select__chip-label sg-label-small-regular">{{ lastSelectedOption.label }}</span>
                  <button
                    type="button"
                    class="sg-auto-complete-select__chip-remove"
                    tabindex="-1"
                    (mousedown)="$event.preventDefault()"
                    (click)="removeChip(lastSelectedOption, $event)"
                    [attr.aria-label]="'Remove ' + lastSelectedOption.label">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              }
            </div>
          }
        </span>
      } @else {
        @if (!label || isOpen || searchQuery) {
          <span class="sg-auto-complete-select__placeholder" [class.sg-body-medium-regular]="size !== 'small'" [class.sg-body-small-regular]="size === 'small'">
            {{ placeholder }}
          </span>
        }
      }
    }
    @if (showClearButton) {
      <button
        type="button"
        class="sg-auto-complete-select__clear"
        tabindex="-1"
        (mousedown)="$event.preventDefault()"
        (click)="clear($event)"
        aria-label="Clear selection">
        <mat-icon>close</mat-icon>
      </button>
    }
    <mat-icon class="sg-auto-complete-select__arrow" [class.sg-auto-complete-select__arrow--open]="isOpen">expand_more</mat-icon>
  </div>
</div>
<!-- Subscript area -->
@if (subscriptSizing === "fixed" || (subscriptSizing === "dynamic" && (hint || error))) {
  <div class="sg-auto-complete-select__subscript">
    @if (hint && !error) {
      <span class="sg-auto-complete-select__hint sg-body-xsmall-regular" @subscriptAnimation>{{ hint }}</span>
    }
    @if (error) {
      <span class="sg-auto-complete-select__error sg-body-xsmall-regular" @subscriptAnimation>{{ error }}</span>
    }
    @if (subscriptSizing === "fixed" && !hint && !error) {
      <span class="sg-auto-complete-select__spacer sg-body-xsmall-regular">&nbsp;</span>
    }
  </div>
}
<!-- CDK Connected Overlay for dropdown panel -->
<ng-template
  cdk-connected-overlay
  [cdkConnectedOverlayOrigin]="overlayOrigin"
  [cdkConnectedOverlayOpen]="isOpen"
  [cdkConnectedOverlayPositions]="positions"
  [cdkConnectedOverlayWidth]="panelWidth === 'trigger' ? triggerWidth : null"
  [cdkConnectedOverlayMinWidth]="panelWidth === 'auto' ? triggerWidth : null"
  [cdkConnectedOverlayScrollStrategy]="scrollStrategy"
  [cdkConnectedOverlayPanelClass]="panelClasses"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  (backdropClick)="onBackdropClick()">
  <div
    class="sg-auto-complete-select__panel"
    [class.sg-auto-complete-select__panel--small]="size === 'small'"
    [style.--sg-auto-complete-trigger-width]="triggerWidth + 'px'"
    [style.max-height]="maxDisplayItems ? 'none' : null"
    role="listbox"
    [attr.aria-multiselectable]="multiple"
    @panelAnimation>
    <!-- Search input -->
    @if (!hideInput) {
      <div class="sg-auto-complete-select__search">
        <mat-icon class="sg-auto-complete-select__search-icon">sg-search</mat-icon>
        <input
          #searchInput
          type="text"
          class="sg-auto-complete-select__search-input sg-body-medium-regular"
          [class.sg-body-small-regular]="size === 'small'"
          [placeholder]="searchInputPlaceholder"
          [value]="searchQuery"
          autocomplete="off"
          data-test="sg-auto-complete-select-search-input"
          (input)="onSearchInput($event)"
          (keydown)="onSearchInputKeydown($event)" />
      </div>
    }
    <!-- Options list container with scroll listener -->
    <div #optionsList class="sg-auto-complete-select__options" [style.max-height]="getOptionsMaxHeight()" (scroll)="onOptionsScroll($event)">
      <!-- Select All option for multi-select -->
      @if (multiple && flattenedOptions.length > 0) {
        <div
          class="sg-auto-complete-select__option sg-auto-complete-select__option--select-all"
          [class.sg-auto-complete-select__option--selected]="selectAllState === 'all'"
          [class.sg-auto-complete-select__option--indeterminate]="selectAllState === 'indeterminate'"
          role="option"
          (click)="toggleSelectAll()">
          <mat-icon class="sg-auto-complete-select__option-checkbox">{{ selectAllIcon }}</mat-icon>
          <span class="sg-auto-complete-select__option-label sg-body-medium-bold">Select all</span>
        </div>
      }
      @for (group of groupedOptions; track group; let groupIndex = $index; let isFirst = $first) {
        <!-- Divider between groups (only show for grouped items with multiple groups, not before first group) -->
        @if (isGrouped && !isFirst && groupedOptions.length > 1) {
          <div class="sg-auto-complete-select__group-divider"></div>
        }
        <!-- Group header (only show if grouped and has label) -->
        @if (isGrouped && group.label) {
          <div
            class="sg-auto-complete-select__group-header"
            [class.sg-auto-complete-select__group-header--highlighted]="isGroupHighlighted(group)"
            role="option"
            tabindex="-1"
            (click)="toggleGroup(group)"
            (mouseenter)="highlightedIndex = getGroupNavigableIndex(group)">
            <!-- Expand/collapse chevron -->
            <mat-icon class="sg-auto-complete-select__group-chevron" [class.sg-auto-complete-select__group-chevron--collapsed]="!group.expanded">
              expand_more
            </mat-icon>
            <!-- Group checkbox for multi-select -->
            @if (multiple) {
              <mat-icon
                class="sg-auto-complete-select__group-checkbox"
                [class.sg-auto-complete-select__group-checkbox--selected]="isGroupSelected(group) === 'all'"
                [class.sg-auto-complete-select__group-checkbox--indeterminate]="isGroupSelected(group) === 'indeterminate'"
                (click)="selectGroup(group); $event.stopPropagation()">
                {{ getGroupCheckboxIcon(group) }}
              </mat-icon>
            }
            <!-- Group label -->
            <span class="sg-auto-complete-select__group-label sg-body-small-bold">{{ group.label }}</span>
          </div>
        }
        <!-- Options (collapse when group is not expanded) -->
        @if (!isGrouped || group.expanded !== false) {
          <div class="sg-auto-complete-select__group-options" [@groupCollapseAnimation] [@.disabled]="groupAnimationDisabled">
            @for (option of group.options; track option; let optionIndex = $index) {
              <div
                class="sg-auto-complete-select__option"
                [class.sg-auto-complete-select__option--selected]="isSelected(option)"
                [class.sg-auto-complete-select__option--highlighted]="highlightedIndex === getNavigableIndex(groupIndex, optionIndex)"
                [class.sg-auto-complete-select__option--custom]="optionDirective"
                role="option"
                [attr.aria-selected]="isSelected(option)"
                data-test="sg-auto-complete-select-option"
                (click)="selectOption(option)"
                (mouseenter)="highlightedIndex = getNavigableIndex(groupIndex, optionIndex)">
                <!-- Checkbox for multi-select -->
                @if (multiple) {
                  <mat-icon class="sg-auto-complete-select__option-checkbox">{{ isSelected(option) ? "check_box" : "check_box_outline_blank" }}</mat-icon>
                }
                <!-- Custom option template or default -->
                @if (optionDirective) {
                  <span class="sg-auto-complete-select__option-content">
                    <ng-container *ngTemplateOutlet="optionDirective.templateRef; context: getOptionContext(option)"></ng-container>
                  </span>
                } @else {
                  <span class="sg-auto-complete-select__option-label sg-body-medium-regular">{{ getOptionLabel(option) }}</span>
                }
                <!-- Selected checkmark for single select -->
                @if (!multiple && isSelected(option)) {
                  <mat-icon class="sg-auto-complete-select__option-check">check</mat-icon>
                }
              </div>
            }
          </div>
        }
      }
      <!-- Empty state -->
      @if (flattenedOptions.length === 0) {
        <div class="sg-auto-complete-select__empty sg-body-medium-regular">No options available</div>
      }
    </div>
    <!-- Actions footer (Apply/Clear buttons) for deferred multi-select mode only -->
    @if (!instantApply && multiple) {
      <div class="sg-auto-complete-select__actions">
        <div class="sg-auto-complete-select__actions-buttons">
          <button
            type="button"
            class="sg-auto-complete-select__action-button sg-auto-complete-select__action-button--clear"
            (click)="clearPendingChanges()"
            [disabled]="selectedCount === 0"
            data-test="sg-auto-complete-select-clear-button">
            <span class="sg-label-small-regular">Clear</span>
          </button>
          <button
            type="button"
            class="sg-auto-complete-select__action-button sg-auto-complete-select__action-button--apply"
            (click)="applyChanges()"
            [disabled]="!hasPendingChanges"
            data-test="sg-auto-complete-select-apply-button">
            <span class="sg-label-small-regular">Apply</span>
          </button>
        </div>
      </div>
    }
    <!-- Async loading template (progress bar at bottom of panel) -->
    @if (asyncTemplateDirective) {
      <ng-container *ngTemplateOutlet="asyncTemplateDirective.templateRef"></ng-container>
    }
  </div>
</ng-template>
